import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Title, Paper, Text, Group, SimpleGrid, ThemeIcon } from '@mantine/core';
import { Target, TreeStructure, CurrencyDollar, Globe, Minus, Users, ArrowRight, Plugs, MagnifyingGlass, Image, UsersThree } from '@phosphor-icons/react';
import SEO from '../components/SEO';
import GoogleAdsSetupGuide from './guides/GoogleAdsSetupGuide';
import ConversionGuide from './ConversionGuide';
import CampaignStructureGuide from './guides/CampaignStructureGuide';
import BiddingGuide from './guides/BiddingGuide';
import LandingPageGuide from './guides/LandingPageGuide';
import NegativeKeywordsGuide from './guides/NegativeKeywordsGuide';
import RemarketingGuide from './guides/RemarketingGuide';
import KeywordMatchTypesGuide from './guides/KeywordMatchTypesGuide';
import MetaAdsSetupGuide from './guides/MetaAdsSetupGuide';
import MetaAudienceGuide from './guides/MetaAudienceGuide';
import MetaCreativeGuide from './guides/MetaCreativeGuide';

const GUIDE_GROUPS = [
  {
    groupKey: 'googleFirstSteps',
    guides: [
      { id: 'google-ads-setup', icon: Plugs, color: 'brand', component: GoogleAdsSetupGuide },
      { id: 'conversions', icon: Target, color: 'green', component: ConversionGuide },
      { id: 'campaign-structure', icon: TreeStructure, color: 'blue', component: CampaignStructureGuide },
      { id: 'keyword-match-types', icon: MagnifyingGlass, color: 'violet', component: KeywordMatchTypesGuide },
    ],
  },
  {
    groupKey: 'googleOptimization',
    guides: [
      { id: 'bidding', icon: CurrencyDollar, color: 'orange', component: BiddingGuide },
      { id: 'negative-keywords', icon: Minus, color: 'red', component: NegativeKeywordsGuide },
      { id: 'landing-pages', icon: Globe, color: 'brand', component: LandingPageGuide },
    ],
  },
  {
    groupKey: 'googleAdvanced',
    guides: [
      { id: 'remarketing', icon: Users, color: 'violet', component: RemarketingGuide },
    ],
  },
  {
    groupKey: 'metaFirstSteps',
    guides: [
      { id: 'meta-ads-setup', icon: Plugs, color: 'indigo', component: MetaAdsSetupGuide },
    ],
  },
  {
    groupKey: 'metaTargeting',
    guides: [
      { id: 'meta-audience', icon: UsersThree, color: 'blue', component: MetaAudienceGuide },
    ],
  },
  {
    groupKey: 'metaCreatives',
    guides: [
      { id: 'meta-creatives', icon: Image, color: 'green', component: MetaCreativeGuide },
    ],
  },
];

const ALL_GUIDES = GUIDE_GROUPS.flatMap(g => g.guides);

function GuideCard({ guide, onClick }) {
  const { t } = useTranslation();
  const Icon = guide.icon;
  return (
    <Paper
      withBorder p="md" radius="md"
      style={{ cursor: 'pointer', transition: 'box-shadow 0.15s' }}
      className="campaign-row"
      onClick={onClick}
    >
      <Group gap="sm" wrap="nowrap" align="flex-start">
        <ThemeIcon variant="light" color={guide.color} size="lg" radius="md">
          <Icon size={20} weight="duotone" />
        </ThemeIcon>
        <Box style={{ flex: 1 }}>
          <Text size="sm" fw={600} mb={2}>{t(`guidesPanel.guides.${guide.id}.title`)}</Text>
          <Text size="xs" c="dimmed" lh={1.5}>{t(`guidesPanel.guides.${guide.id}.description`)}</Text>
        </Box>
        <ArrowRight size={14} className="text-gray-400" style={{ flexShrink: 0, marginTop: 4 }} />
      </Group>
    </Paper>
  );
}

export default function Guides() {
  const { t } = useTranslation();
  const { guideId } = useParams();
  const navigate = useNavigate();

  const activeGuide = ALL_GUIDES.find(g => g.id === guideId);
  if (activeGuide) {
    const Component = activeGuide.component;
    return <Component />;
  }

  return (
    <Box px="lg" py="md">
      <SEO title={t('guidesPanel.seoTitle')} path="/app/guides" noIndex />
      <Title order={3} mb="md">{t('guidesPanel.heading')}</Title>

      {GUIDE_GROUPS.map(group => (
        <Box key={group.groupKey} mb="lg">
          <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb="sm" style={{ letterSpacing: '0.05em' }}>{t(`guidesPanel.groups.${group.groupKey}`)}</Text>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="sm">
            {group.guides.map(guide => (
              <GuideCard key={guide.id} guide={guide} onClick={() => navigate(`/app/guides/${guide.id}`)} />
            ))}
          </SimpleGrid>
        </Box>
      ))}
    </Box>
  );
}
